import { computed, inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { partition } from 'lodash-es';
import {
  ANONYMOUS_USER_ID,
  SELECTED_LANGUAGE_PRIMARY,
  SELECTED_LANGUAGE_SECONDARY,
} from 'src/app/@constants/local-storage-key-constants';
import { Language } from 'src/app/@models/language/language.model';
import { LanguageActivityService } from 'src/app/@services/language-activity.service';
import { EventConfigStateService } from 'src/app/@store/event-config-state.service';
import { ExternalRegistrationStateService } from 'src/app/@store/external-registration-state.service';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from 'src/app/@utils/local-storage-util';
import { sameLanguage } from 'src/app/@utils/language-code';

interface LanguageState {
  availableLanguages: Language[];
  originalLanguageCode: string;
  selectedLanguage: Language;
}

const initialState: LanguageState = {
  availableLanguages: [],
  originalLanguageCode: 'en',
  selectedLanguage: {
    code: 'en',
    value: 'English',
    label: 'EN - English',
  },
};

const state = {
  availableLanguages: signal<Language[]>(initialState.availableLanguages),
  selectedLanguage: signal<Language>(initialState.selectedLanguage),
  originalLanguageCode: signal<string>(initialState.originalLanguageCode),
};

@Injectable({
  providedIn: 'root',
})
export class LanguageStore {
  public readonly $originalLanguageCode =
    state.originalLanguageCode.asReadonly();
  public readonly $selectedLanguage = state.selectedLanguage.asReadonly();
  public readonly $availableLanguages = computed(() => {
    const languages = state.availableLanguages();
    const originalCode = state.originalLanguageCode();
    const [originalLanguages, otherLanguages] = partition(
      languages,
      (lang) => lang.code === originalCode
    );
    const [ttsLanguages, nonTtsLanguages] = partition(
      otherLanguages,
      (lang) => lang.ttsEnabled
    );
    return [...originalLanguages, ...ttsLanguages, ...nonTtsLanguages];
  });

  private readonly _eventConfigStore = inject(EventConfigStateService);
  private readonly _externalRegistrationState = inject(
    ExternalRegistrationStateService
  );
  private readonly _languageActivityService = inject(LanguageActivityService);
  private readonly _route = inject(ActivatedRoute);

  setAvailableLanguages(languages: Language[]): void {
    state.availableLanguages.set(languages);
  }

  setOriginalLanguageCode(languageCode: string): void {
    state.originalLanguageCode.set(languageCode);
    this._initializeSelectedLanguage();
  }

  setSelectedLanguage(language: Language): void {
    const previousLanguage = state.selectedLanguage();

    // Track all language changes (except initial selection)
    if (previousLanguage.code !== language.code) {
      this._trackLanguageChange(previousLanguage, language);
    }

    state.selectedLanguage.set(language);
    setLocalStorageItem(this._getStorageKey(), language);
  }

  private _initializeSelectedLanguage(): void {
    const isPrimaryScreen = this._eventConfigStore.$isPrimaryScreen();
    // dev→freeze merge: keep freeze's namespaced storage key (_getStorageKey,
    // matches the setLocalStorageItem write above) AND develop's validation
    // inputs (originalLanguageCode + availableLanguages) used by the stale-
    // entry check below — neither side's function is dropped.
    const selectedLanguageFromStorage = getLocalStorageItem<Language>(
      this._getStorageKey()
    );
    const originalLanguageCode = state.originalLanguageCode();
    const availableLanguages = state.availableLanguages() ?? [];

    if (selectedLanguageFromStorage && !isPrimaryScreen) {
      // Validate the saved selection against the current event's
      // availableLanguages OR the OriginalLanguageCode. Stale entries from a
      // previous event (e.g. user picked `tr-TR` at Event A, now visits Event
      // B which only supports `en` + `es`) would otherwise cause silent
      // English fallbacks because the WS never delivers Turkish translations.
      const storedCode = selectedLanguageFromStorage.code;
      const matchedAvailable = availableLanguages.find((l) =>
        sameLanguage(l.code, storedCode)
      );
      const isOriginal = sameLanguage(storedCode, originalLanguageCode);
      if (matchedAvailable || isOriginal) {
        // Prefer the CURRENT event's language object over the stored one.
        // The stored object can carry a stale regional code (e.g. `tr-TR` when
        // this event exposes `tr`) and stale metadata (e.g. `ttsEnabled`) from a
        // previous event. Resolve to: matching availableLanguages entry → the
        // availableLanguages entry for OriginalLanguageCode → a synthesized
        // original-language object → (last resort) the stored object. Re-persist
        // so the stale entry is overwritten.
        const fresh: Language =
          matchedAvailable ??
          availableLanguages.find((l) =>
            sameLanguage(l.code, originalLanguageCode)
          ) ??
          (originalLanguageCode
            ? {
                code: originalLanguageCode,
                value: originalLanguageCode,
                label: originalLanguageCode.toUpperCase(),
              }
            : selectedLanguageFromStorage);
        state.selectedLanguage.set(fresh);
        setLocalStorageItem(this._getStorageKey(), fresh);
        return;
      }
      console.warn(
        `[LanguageStore] Stored selection "${storedCode}" is neither in availableLanguages nor matches OriginalLanguageCode "${originalLanguageCode}". Clearing.`
      );
      removeLocalStorageItem(this._getStorageKey());
    }

    // Prefer a language from availableLanguages that matches OriginalLanguageCode.
    // If none match (event config not yet loaded, or no entry for the original
    // language), synthesize a minimal Language from OriginalLanguageCode so we
    // never silently fall back to hardcoded 'en' for non-English events.
    const matched = availableLanguages.find((language) =>
      sameLanguage(language.code, originalLanguageCode)
    );
    const language: Language =
      matched ??
      (originalLanguageCode
        ? {
            code: originalLanguageCode,
            value: originalLanguageCode,
            label: originalLanguageCode.toUpperCase(),
          }
        : initialState.selectedLanguage);
    state.selectedLanguage.set(language);
    setLocalStorageItem(this._getStorageKey(), language);
  }

  private _getStorageKey(): string {
    return this._eventConfigStore.$isPrimaryScreen()
      ? SELECTED_LANGUAGE_PRIMARY
      : SELECTED_LANGUAGE_SECONDARY;
  }

  private _trackLanguageChange(fromLang: Language, toLang: Language): void {
    const eventId = this._eventConfigStore.$eventIdentifier();
    const sessionId = this._getSessionIdFromRoute();
    const registrationId = this._externalRegistrationState.$registrationId();
    const userId = registrationId || this._getOrCreateAnonymousId();
    const userType = registrationId ? 'registered' : 'anonymous';
    const isPrimaryScreen = this._eventConfigStore.$isPrimaryScreen();

    this._languageActivityService
      .logLanguageChange({
        eventId,
        sessionId,
        userId,
        userType,
        languageFrom: fromLang.code,
        languageFromLabel: fromLang.label,
        languageTo: toLang.code,
        languageToLabel: toLang.label,
        isPrimaryScreen,
        timestamp: Date.now(),
      })
      .subscribe({
        error: (err) => console.debug('Failed to log language change:', err),
      });
  }

  private _getSessionIdFromRoute(): string {
    let route = this._route;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.paramMap.get('sessionId') || 'unknown';
  }

  private _getOrCreateAnonymousId(): string {
    let anonId = getLocalStorageItem<string>(ANONYMOUS_USER_ID);

    if (!anonId) {
      anonId = `anon-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setLocalStorageItem(ANONYMOUS_USER_ID, anonId);
    }

    return anonId;
  }
}
